/**
 * Static Programs Index
 *
 * This file exports all static program definitions.
 * Used as a fallback when Firebase programs are missing templates.
 */

import { specialForcesSustainment6Week } from './specialForcesSustainment6Week';
import { operatorStrength13WeekReal } from './operatorStrength13WeekReal';
import { gruntProgram12Week } from './gruntProgram12Week';
import { specialistProgram12Week } from './specialistProgram12Week';
import { recruitProgram12Week } from './recruitProgram12Week';
import { bodyweightFoundation12Week } from './bodyweightFoundation12Week';
import { hstFatLoss12Week } from './hstFatLoss12Week';
import { preeclampsiaPrenatalProgram } from './preeclampsiaPrenatal';
import { chassisCutOperatorHybrid } from './chassisCutOperatorHybrid';
import { operatorRampUp2Week } from './operatorRampUp2Week';
import { allTrackingTypesQA } from './allTrackingTypesQA';
import { recruitRampUp1Week } from './recruitRampUp1Week';
import { operatorReset3Week } from './operatorReset3Week';
import { pureStrength12Week } from './pureStrength12Week';
import { enduranceBase12Week } from './enduranceBase12Week';
import { hypertrophy12Week } from './hypertrophy12Week';
import { strengthFoundations12Week } from './strengthFoundations12Week';
import { firstMile12Week } from './firstMile12Week';
import { firstMass12Week } from './firstMass12Week';
import { totalFitness12Week } from './totalFitness12Week';
import { taskforceSpearhead8Week } from './taskforceSpearhead8Week';
import { taskforceSpearheadLight8Week } from './taskforceSpearheadLight8Week';
import type { WorkoutProgram } from '../types/program';

/**
 * Map of program IDs to static program definitions
 */
export const STATIC_PROGRAMS: Record<string, WorkoutProgram> = {
  // Core programs - IDs must match metadata.id in each program file
  'special-forces-sustainment-6week': specialForcesSustainment6Week,
  'operator-strength-13week': operatorStrength13WeekReal,
  'taskforce-spearhead-8week': taskforceSpearhead8Week,
  'taskforce-spearhead-light-8week': taskforceSpearheadLight8Week,
  'grunt-program-12week': gruntProgram12Week,
  'specialist-program-12week': specialistProgram12Week,
  'recruit-program-12week': recruitProgram12Week,
  'bodyweight-foundation-12week': bodyweightFoundation12Week,

  // HST - metadata.id = 'hst-fat-loss-framework'
  'hst-fat-loss-framework': hstFatLoss12Week,
  'hst-fat-loss-12week': hstFatLoss12Week, // Legacy alias

  // Prenatal - metadata.id = 'prenatal-preeclampsia-focused'
  'prenatal-preeclampsia-focused': preeclampsiaPrenatalProgram,
  'preeclampsia-prenatal-37week': preeclampsiaPrenatalProgram, // Legacy alias

  // CHASSIS CUT - metadata.id = 'chassis-cut-operator-hybrid'
  'chassis-cut-operator-hybrid': chassisCutOperatorHybrid,

  // RAMP-UP programs — by difficulty / detraining severity. All bypass the
  // reentry intensity cap (the program IS the ramp).
  'recruit-ramp-up-1week': recruitRampUp1Week,     // beginner / heavy detraining
  'operator-ramp-up-2week': operatorRampUp2Week,   // intermediate (standard)
  // QA fixture — every tracking type in 3 days. isActive:false keeps it out of
  // browse/recommendations; selectable directly for clickthrough verification.
  'qa-all-tracking-types': allTrackingTypesQA,
  'operator-reset-3week': operatorReset3Week,      // advanced / 60+ days off

  // PURE STRENGTH - metadata.id = 'pure-strength-12week'
  'pure-strength-12week': pureStrength12Week,

  // ENDURANCE BASE - metadata.id = 'endurance-base-12week'
  'endurance-base-12week': enduranceBase12Week,

  // HYPERTROPHY BUILDER - metadata.id = 'hypertrophy-builder-12week'
  'hypertrophy-builder-12week': hypertrophy12Week,

  // BEGINNER VARIANTS for non-tactical goals.
  'strength-foundations-12week': strengthFoundations12Week,
  'first-mile-12week': firstMile12Week,
  'first-mass-12week': firstMass12Week,
  'total-fitness-12week': totalFitness12Week,
};

/**
 * Get a static program by ID
 * Returns null if program doesn't exist in static definitions
 */
export function getStaticProgram(programId: string): WorkoutProgram | null {
  return STATIC_PROGRAMS[programId] || null;
}

/**
 * Get all static program IDs
 */
export function getAllStaticProgramIds(): string[] {
  return Object.keys(STATIC_PROGRAMS);
}

/**
 * Check if a program has a static definition
 */
export function hasStaticProgram(programId: string): boolean {
  return programId in STATIC_PROGRAMS;
}
